import React, { useState, useEffect } from "react";
import { Menu, X, Languages } from "lucide-react";
import { Link } from "react-router-dom";
import { LOGO_URL } from "../../data/content";
import { StoreButton } from "./StoreButtons";
import { useLang } from "../../i18n/LanguageContext";

const navLinks = [
  { key: "features", href: "/#features", id: "features" },
  { key: "how", href: "/#so-gehts", id: "so-gehts" },
  { key: "faq", href: "/faq", id: "faq", page: true },
  { key: "privacy", href: "/datenschutz", id: "datenschutz", page: true },
  { key: "terms", href: "/nutzerbedingungen", id: "nutzerbedingungen", page: true },
  { key: "imprint", href: "/impressum", id: "impressum", page: true }
];

const LangToggle = ({ className = "", testId = "lang-toggle" }) => {
  const { lang, toggle } = useLang();
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid={testId}
      aria-label="Sprache wechseln / Switch language"
      className={`inline-flex items-center gap-1.5 rounded-full border border-petrol/15 px-3 py-1.5 text-sm font-bold text-petrol transition-colors duration-200 hover:border-terracotta hover:text-terracotta ${className}`}
    >
      <Languages className="h-4 w-4" />
      {lang === "de" ? "EN" : "DE"}
    </button>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        scrolled
          ? "bg-cream/85 shadow-[0_4px_20px_rgba(15,37,55,0.06)] backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5">
          <img src={LOGO_URL} alt="WasGitsHüt? Logo" className="h-11 w-11 rounded-xl object-contain" />
          <span className="font-heading text-xl font-extrabold text-petrol">
            WasGits<span className="text-terracotta">Hüt?</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-5 lg:flex xl:gap-7">
          {navLinks.map((l) =>
            l.page ? (
              <Link
                key={l.id}
                to={l.href}
                data-testid={`nav-${l.id}`}
                className="text-sm font-bold text-petrol-soft transition-colors duration-200 hover:text-terracotta"
              >
                {t.nav[l.key]}
              </Link>
            ) : (
              <a
                key={l.id}
                href={l.href}
                data-testid={`nav-${l.id}`}
                className="text-sm font-bold text-petrol-soft transition-colors duration-200 hover:text-terracotta"
              >
                {t.nav[l.key]}
              </a>
            )
          )}
        </nav>

        <div className="hidden items-center lg:flex">
          <LangToggle />
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LangToggle testId="lang-toggle-mobile" />
          <button
            className="text-petrol"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-petrol/10 bg-cream/95 px-5 py-4 backdrop-blur-xl lg:hidden">
          <nav className="flex flex-col gap-3">
            {navLinks.map((l) =>
              l.page ? (
                <Link
                  key={l.id}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.id}`}
                  className="py-1 font-bold text-petrol"
                >
                  {t.nav[l.key]}
                </Link>
              ) : (
                <a
                  key={l.id}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.id}`}
                  className="py-1 font-bold text-petrol"
                >
                  {t.nav[l.key]}
                </a>
              )
            )}
            <a href="/#download" onClick={() => setOpen(false)} className="pt-2">
              <StoreButton store="apple" testId="mobile-header-download-button" />
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
